import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import {
  PerkPointTransactionApi,
  InsertTransactionRequest,
} from "@/api/giftcard.api";
import {
  DUMMY_PRODUCTS,
  TRANSACTION_TYPE_OPTIONS,
} from "./dummyProducts";

const transactionFormSchema = z.object({
  p_TransactionType: z.string().min(1, "Transaction type is required"),
  p_CounterId: z.coerce.number().min(0, "Counter ID is required"),
  p_MobileNo: z
    .string()
    .min(7, "Enter a valid mobile number")
    .max(15, "Mobile number is too long"),
  p_InvoiceNo: z.string().min(1, "Invoice number is required"),
  p_InvoiceAmount: z.coerce.number().min(0, "Invoice amount is required"),
  p_CustomerName: z.string().min(1, "Customer name is required"),
  p_BurnPoints: z.coerce.number().min(0, "Burn points must be 0 or greater"),
});

type TransactionFormValues = z.infer<typeof transactionFormSchema>;

export default function InsertTransaction() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProductCode, setSelectedProductCode] = useState<string>("");
  const [cartQuantities, setCartQuantities] = useState<Record<string, number>>(
    {}
  );

  const form = useForm<TransactionFormValues>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: {
      p_TransactionType: "",
      p_CounterId: 1,
      p_MobileNo: "",
      p_InvoiceNo: "",
      p_InvoiceAmount: 0,
      p_CustomerName: "",
      p_BurnPoints: 0,
    },
  });

  const transactionType = form.watch("p_TransactionType");
  const isBurnTransaction = transactionType === "Burn";

  const selectedProducts = useMemo(
    () =>
      DUMMY_PRODUCTS.filter((p) => (cartQuantities[p.productCode] ?? 0) > 0).map(
        (p) => ({
          ...p,
          productQuantity: cartQuantities[p.productCode],
        })
      ),
    [cartQuantities]
  );

  const handleAddProduct = () => {
    if (!selectedProductCode) {
      toast({
        title: "Select a product",
        description: "Choose a product from the dropdown to add.",
        variant: "destructive",
      });
      return;
    }
    setCartQuantities((prev) => ({
      ...prev,
      [selectedProductCode]: (prev[selectedProductCode] ?? 0) + 1,
    }));
  };

  const updateQuantity = (productCode: string, delta: number) => {
    setCartQuantities((prev) => {
      const current = prev[productCode] ?? 0;
      const next = Math.max(0, current + delta);
      if (next === 0) {
        const { [productCode]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productCode]: next };
    });
  };

  const removeProduct = (productCode: string) => {
    setCartQuantities((prev) => {
      const { [productCode]: _, ...rest } = prev;
      return rest;
    });
  };

  const resetCart = () => {
    setCartQuantities({});
    setSelectedProductCode("");
  };

  const onSubmit = async (data: TransactionFormValues) => {
    if (selectedProducts.length === 0) {
      toast({
        title: "No products selected",
        description: "Add at least one product with quantity greater than 0.",
        variant: "destructive",
      });
      return;
    }

    const txnDateTime = new Date().toISOString();
    const payload: InsertTransactionRequest = {
      transactionDetails: {
        p_TransactionType: data.p_TransactionType,
        p_CounterId: data.p_CounterId,
        p_MobileNo: data.p_MobileNo,
        p_InvoiceNo: data.p_InvoiceNo,
        p_InvoiceAmount: data.p_InvoiceAmount,
        p_CustomerName: data.p_CustomerName,
        p_BurnPoints: data.p_TransactionType === "Burn" ? data.p_BurnPoints : 0,
      },
      listProductDetails: selectedProducts.map((p) => ({
        productCode: p.productCode,
        productName: p.productName,
        productAmount: p.productAmount,
        productQuantity: p.productQuantity,
        txnDateTime,
      })),
    };

    setIsSubmitting(true);
    try {
      const response = await PerkPointTransactionApi.insertTransaction(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description:
            response.message || "Transaction inserted successfully",
        });
        form.reset({
          p_TransactionType: "",
          p_CounterId: 1,
          p_MobileNo: "",
          p_InvoiceNo: "",
          p_InvoiceAmount: 0,
          p_CustomerName: "",
          p_BurnPoints: 0,
        });
        resetCart();
      }
    } catch (error) {
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to insert transaction",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Transaction</h1>
        <p className="text-muted-foreground">
          Insert a new transaction with customer details and product line items
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Transaction Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="p_TransactionType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Transaction Type</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        if (value !== "Burn") {
                          form.setValue("p_BurnPoints", 0);
                        }
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TRANSACTION_TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="p_CounterId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Counter ID</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="p_CustomerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter customer name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="p_MobileNo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile Number</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="Enter mobile number"
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="p_InvoiceNo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter invoice number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="p_InvoiceAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invoice Amount</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isBurnTransaction ? (
                <FormField
                  control={form.control}
                  name="p_BurnPoints"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Burn Points</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step="1"
                          placeholder="Points to redeem / burn"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingCart className="h-5 w-5" />
                  Products
                </CardTitle>
                <span className="text-sm text-muted-foreground">
                  {selectedProducts.length} item(s)
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1 space-y-2">
                  <label className="text-sm font-medium">Add Product</label>
                  <Select
                    value={selectedProductCode}
                    onValueChange={setSelectedProductCode}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {DUMMY_PRODUCTS.map((product) => (
                        <SelectItem
                          key={product.productCode}
                          value={product.productCode}
                        >
                          {product.productName} ({product.productCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0 border-green-600 text-green-700 hover:bg-green-50"
                  onClick={handleAddProduct}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>

              {selectedProducts.length > 0 ? (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm font-medium text-gray-700">
                    Added products
                  </p>
                  {selectedProducts.map((product) => {
                    const qty = cartQuantities[product.productCode] ?? 0;
                    return (
                      <div
                        key={product.productCode}
                        className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {product.productName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Code: {product.productCode}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              updateQuantity(product.productCode, -1)
                            }
                            disabled={qty <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[2rem] text-center font-semibold">
                            {qty}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              updateQuantity(product.productCode, 1)
                            }
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeProduct(product.productCode)}
                            aria-label={`Remove ${product.productName}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground border-t pt-4">
                  Select a product from the dropdown and click Add to build your
                  line items.
                </p>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => {
                form.reset();
                resetCart();
              }}
            >
              Reset
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              {isSubmitting ? "Submitting..." : "Insert Transaction"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
